import React from "react";

// Every knob of RunSettings (backend/app/settings.py), grouped by pipeline stage.
// The stop conditions sit up top; everything else is under "advanced".
export const STOP_FIELDS = [
  { k: "budget_usd", label: "Budget", unit: "$", min: 0.05, hint: "Apify spend cap for the whole run" },
  { k: "target_leads", label: "Target leads", min: 1, hint: "stop once this many matches are confirmed" },
  { k: "max_rounds", label: "Max rounds", min: 0, hint: "0 = no limit. A round: source → profiles → filter → judge → expand" },
];

// Simple view: the goal and the hard spend cap, nothing else.
export const SIMPLE_FIELDS = [
  { k: "target_leads", label: "Accounts to find", min: 1 },
  { k: "budget_usd", label: "Max budget", unit: "$", min: 0.05 },
];

export const GROUPS = [
  {
    title: "Sourcing",
    fields: [
      { k: "use_keywords", label: "Keyword search", type: "bool" },
      { k: "use_hashtags", label: "Hashtags", type: "bool" },
      {
        k: "keywords_first",
        label: "Keywords first",
        type: "bool",
        hint: "hashtags wait until a round of keyword search finds nothing in range (they cost about 2x per account)",
      },
      { k: "keywords_per_round", label: "Keywords per round", min: 0, hint: "searched 5 per Apify run, in parallel" },
      { k: "hashtags_per_round", label: "Hashtags held ready", min: 0, hint: "used once keywords run dry" },
      { k: "results_per_keyword", label: "Results per keyword", min: 5, hint: "most keywords return under 20; the full cap is set aside from the budget while it runs" },
      { k: "posts_per_hashtag", label: "Posts per hashtag", min: 10, hint: "first look at a tag; tags that produce in-range accounts get re-run 3× deeper" },
    ],
  },
  {
    title: "Expansion between rounds",
    fields: [
      { k: "harvest_hashtags", label: "Harvest hashtags from good accounts", type: "bool" },
      { k: "follow_mentions", label: "Follow @mentions of good accounts", type: "bool" },
      { k: "deepen_hashtags", label: "Deepen productive hashtags", type: "bool", hint: "re-run a tag that found a match or 2+ in-range accounts at 3× depth" },
      { k: "near_miss_score", label: "Near-miss score", min: 0, hint: "judged accounts at or above this seed expansion" },
    ],
  },
  {
    title: "Profiles",
    fields: [
      { k: "profiles_per_round", label: "Profiles per round", min: 10, hint: "most profiles paid for per round" },
      { k: "profile_cache_days", label: "Profile cache (days)", min: 0, hint: "reuse profiles any run fetched recently, free" },
    ],
  },
  {
    title: "Judge",
    fields: [
      { k: "min_score", label: "Min match score", min: 0, hint: "a match also needs at least this confidence" },
      { k: "judge_batch_size", label: "Profiles per Claude call", min: 1 },
    ],
  },
];

const PRICE = 0.0023; // Apify $/result, all three actors (backend config.APIFY_PRICE_PER_RESULT)

// Worst case for one round at these settings: every query returns its full depth
// and the full profile quota gets fetched.
export const roundCostCeiling = (s) => {
  if (!s) return 0;
  const kw = s.use_keywords ? s.keywords_per_round * s.results_per_keyword : 0;
  const tags = s.use_hashtags ? s.hashtags_per_round * s.posts_per_hashtag : 0;
  return (kw + tags + s.profiles_per_round) * PRICE;
};

// Inputs use step="any": a real step makes the browser reject values off its grid
// (with min 0.05 and step 0.5, even $2 fails), which silently blocks submit.
function Field({ f, value, onChange }) {
  if (f.type === "bool") {
    return (
      <label className="sf-bool" title={f.hint || ""}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(f.k, e.target.checked)} />
        <span>{f.label}</span>
      </label>
    );
  }
  return (
    <label className="sf-num" title={f.hint || ""}>
      <span className="sf-label">{f.label}</span>
      <span className="sf-input">
        {f.unit && <span className="sf-unit">{f.unit}</span>}
        <input
          type="number"
          step="any"
          min={f.min}
          value={value ?? ""}
          onChange={(e) => onChange(f.k, e.target.value === "" ? "" : Number(e.target.value))}
        />
      </span>
      {f.hint && <span className="sf-hint">{f.hint}</span>}
    </label>
  );
}

export function StopFields({ value, onChange, fields = STOP_FIELDS }) {
  return (
    <div className="sf-stop">
      {fields.map((f) => (
        <Field key={f.k} f={f} value={value?.[f.k]} onChange={onChange} />
      ))}
    </div>
  );
}

export function AdvancedFields({ value, onChange }) {
  return (
    <div className="sf-groups">
      {GROUPS.map((g) => (
        <div key={g.title} className="sf-group">
          <div className="sf-group-title">{g.title}</div>
          {g.fields.map((f) => (
            <Field key={f.k} f={f} value={value?.[f.k]} onChange={onChange} />
          ))}
        </div>
      ))}
    </div>
  );
}
